import type { FC } from "react"
import React, { useEffect, useState } from "react"
import {
  Button,
  Flex,
  Stack,
  HStack,
  FormHelperText,
  Text,
  Image,
  FormControl,
  Input,
  Box,
  Skeleton,
  Icon,
} from "@chakra-ui/react"
import type { Pod } from "@orcaprotocol/orca-sdk"
import { init, getAdminPods } from "@orcaprotocol/orca-sdk"
import { isAddress } from "@ethersproject/address"
import type { FieldProps } from "formik"
import { Field, useFormikContext } from "formik"
import invariant from "tiny-invariant"
import { getProvider } from "@wagmi/core"
import { useProvider } from "wagmi"

import type ManageOrcaPodAction from "createProposal/action/ManageOrcaPodAction"
import { useManageOrcaPodRecipeQuery } from "query/graphql"
import CreateProposalFormError from "createProposal/components/CreateProposalFormError"
import type { CreateProposalFormValues } from "createProposal/types/form"
import { CREATE_PROPOSAL_ERRORS } from "errors/createProposal"
import { getAccountAddress, getAccountId } from "web3/helpers/accountId"
import { getChainIdParams } from "web3/helpers/chainId"
import { useOrcaPodController } from "web3/hooks/useOrcaPodController"
import GreenCircleCheck from "ui/components/icons/GreenCircleCheck"
import { generateArrayOfNumbers } from "common/helpers/array"

type ManageOrcaPodProps = {
  action: ManageOrcaPodAction
  index: number
  timelockId: string | undefined | null
  chainId: string | undefined | null
}

const ManageOrcaPodRecipe: FC<ManageOrcaPodProps> = ({
  action,
  index,
  timelockId,
  chainId,
}) => {
  const [pods, setPods] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const timelockAddress = timelockId ? getAccountAddress(timelockId) : ""

  const { setFieldValue } = useFormikContext<CreateProposalFormValues>()

  invariant(chainId, "chaindId must exist")

  const chainIdParams = getChainIdParams(chainId)

  const { reference } = chainIdParams

  const provider = getProvider({ chainId: reference })

  init({ provider: provider, network: reference })

  const getOrcaPods = async () => {
    setIsLoading(true)
    const pods = await getAdminPods(timelockAddress)
    setPods(pods as [])
    setIsLoading(false)

    return
  }

  useEffect(() => {
    getOrcaPods()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack spacing={2}>
      {action.pod ? (
        <Stack spacing={4}>
          <Text as="h6" textStyle="h6">
            {action?.pod?.ensName}
          </Text>
          (
          <ManagePodMembers
            action={action}
            chainId={chainId}
            index={index}
            pod={action.pod}
          />
          )
          <Button
            variant="outline"
            w={28}
            onClick={() => {
              setFieldValue(`actions.${index}.pod`, undefined)
            }}
          >
            <Text>Switch Pod</Text>
          </Button>
        </Stack>
      ) : (
        <Stack spacing={4}>
          <Text>
            Select the pod you would like to add or remove members from:
          </Text>
          <PodSelector index={index} isLoading={isLoading} pods={pods} />
        </Stack>
      )}
    </Stack>
  )
}

type PodSelectorProps = {
  pods: Pod[]
  index: number
  isLoading: boolean
}

const PodSelector: FC<PodSelectorProps> = ({ pods, index, isLoading }) => {
  const { setFieldValue } = useFormikContext<CreateProposalFormValues>()

  return (
    <HStack flexWrap="wrap" spacing={0}>
      {isLoading
        ? generateArrayOfNumbers(4).map((number) => (
            <Flex key={`pod_img_${number}`} h="174px" pb={3} pr={3} w="25%">
              <Skeleton h="full" w="full" />
            </Flex>
          ))
        : pods.map((pod: Pod) => {
            return (
              <Box
                key={pod.ensName}
                as="button"
                pb={3}
                pr={3}
                w="25%"
                onClick={() => {
                  setFieldValue(`actions.${index}.pod`, pod)
                }}
              >
                <Image alt={`${pod.ensName} image`} src={pod.imageUrl} />
              </Box>
            )
          })}
      {pods.length < 1 && !isLoading && <Text>No pods found.</Text>}
    </HStack>
  )
}

type ManagePodMembersProps = {
  pod: Pod
  index: number
  action: ManageOrcaPodAction
  chainId: string | undefined | null
}

const ManagePodMembers: FC<ManagePodMembersProps> = ({
  action,
  pod,
  index,
  chainId,
}) => {
  const [members, setMembers] = useState([])

  const provider = useProvider()
  const orcaPodController = useOrcaPodController(pod?.controller, provider)

  const getMemberToken = async () => {
    const memberToken = await orcaPodController?.memberToken()
    setFieldValue(`actions.${index}.targetAddress`, memberToken)

    return
  }

  useEffect(() => {
    getMemberToken()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { setFieldValue, values } = useFormikContext<CreateProposalFormValues>()

  useEffect(() => {
    const changedValues =
      action.addedMembers.length + action.removedMembers.length
    setFieldValue(`actions.${index}.changedMembers`, changedValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  const getPodMembers = async () => {
    const members = await pod.getMembers()

    setMembers(members as [])

    return
  }

  useEffect(() => {
    getPodMembers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  invariant(chainId, "chaindId must exist")

  const accountIds = members.map((member) => {
    return getAccountId({ address: member, chainId })
  })

  const { data } = useManageOrcaPodRecipeQuery({ accountIds })

  return (
    <Stack spacing={1}>
      {members &&
        members.map((member, idx) => {
          const isRemoved = action.removedMembers.includes(member)
          const ensName = data?.accounts[idx].ens
          const isWiped =
            action.removedMembers.length + 1 ===
            members.length + action.addedMembers.length

          return (
            <Flex
              key={member}
              align="center"
              bg={isRemoved ? "red.50" : "gray.50"}
              justify="space-between"
              pl={4}
            >
              <Text>{ensName ? ensName : member}</Text>

              {isRemoved ? (
                <Button
                  bg="gray.50"
                  w={24}
                  onClick={() => {
                    setFieldValue(
                      `actions.${index}.removedMembers`,
                      action.removedMembers.filter((e) => e !== member),
                    )
                  }}
                >
                  <Text>Undo</Text>
                </Button>
              ) : (
                <Button
                  bg="red.50"
                  isDisabled={isWiped}
                  w={24}
                  onClick={() => {
                    setFieldValue(`actions.${index}.removedMembers`, [
                      ...action.removedMembers,
                      member,
                    ])
                  }}
                >
                  <Text>Remove</Text>
                </Button>
              )}
            </Flex>
          )
        })}
      {action.addedMembers &&
        action.addedMembers.map((addedMember) => {
          return (
            <Flex
              key={addedMember}
              align="center"
              bg="green.50"
              h={10}
              justify="space-between"
              pl={4}
            >
              <Text>{addedMember}</Text>
              <Button
                bg="transparent"
                w={24}
                onClick={() => {
                  setFieldValue(
                    `actions.${index}.addedMembers`,
                    action.addedMembers.filter((e) => e !== addedMember),
                  )
                }}
              >
                <Text>Undo</Text>
              </Button>
            </Flex>
          )
        })}
      <AddedAddressInput action={action} index={index} />
    </Stack>
  )
}

type AddedAddressInputProps = {
  index: number
  action: ManageOrcaPodAction
}

const AddedAddressInput: FC<AddedAddressInputProps> = ({ action, index }) => {
  const handleValidate = (value: string) => {
    if (!isAddress(value) && value) {
      const addedMemberValidError =
        CREATE_PROPOSAL_ERRORS.actions.addedMember.invalidAddressError()

      return addedMemberValidError.displayValue
    }

    if (action.addedMembers.includes(value as never)) {
      const duplicateAddressError =
        CREATE_PROPOSAL_ERRORS.actions.addedMember.duplicateAddressError()

      return duplicateAddressError.displayValue
    }

    return
  }

  const { setFieldValue } = useFormikContext<CreateProposalFormValues>()

  const handleAddMember = () => {
    const nextAddedMembers = [...action.addedMembers, action.addedMember]
    setFieldValue(`actions.${index}.addedMembers`, nextAddedMembers)
    setFieldValue(`actions.${index}.addedMember`, "")
  }

  return (
    <Field name={`actions.${index}.addedMember`} validate={handleValidate}>
      {({ field, meta }: FieldProps) => {
        const { name, value, onBlur, onChange } = field

        const successText = "Valid address"

        const hasValue = Boolean(value)
        const isInvalid = Boolean(meta.error && value)
        const isValid = Boolean(!meta.error && value)

        return (
          <FormControl isInvalid={isInvalid}>
            <Flex align="center" bg="gray.50" justify="space-between">
              <Input
                borderColor={isValid ? "green.500" : "inherit"}
                name={name}
                placeholder="Enter a member address..."
                value={value}
                w="88%"
                onBlur={onBlur}
                onChange={onChange}
              />
              <Button
                isDisabled={isInvalid || !hasValue}
                ml={0.5}
                w={24}
                onClick={handleAddMember}
              >
                <Text>Add</Text>
              </Button>
            </Flex>
            {isValid && (
              <FormHelperText>
                <HStack>
                  <Icon as={GreenCircleCheck} h={4} w={4} />
                  <Text color="gray.700">{successText}</Text>
                </HStack>
              </FormHelperText>
            )}
            <CreateProposalFormError error={meta.error} />
          </FormControl>
        )
      }}
    </Field>
  )
}

export default ManageOrcaPodRecipe
